import React, { useCallback, useEffect, useMemo, useRef, useState } from 'react';
import { useTheme } from './ThemeContext';
import Sidebar from './components/Sidebar';
import Header from './components/Header';
import MainContent from './components/MainContent';
import ChatPanel from './components/ChatPanel';
import { collection, query, onSnapshot, doc, limit, deleteDoc } from 'firebase/firestore';
import { db } from './firebaseConfig';
import AlertModal from './components/AlertModal';

function Dashboard() {
  const { colors } = useTheme();
  const [activeSection, setActiveSection] = useState('dashboard');
  const [isCollapsed, setIsCollapsed] = useState(false);
  const [operadorAutenticado, setOperadorAutenticado] = useState(null);
  const [activeAlert, setActiveAlert] = useState(null);
  const [reporteDiario, setReporteDiario] = useState({
    viajesRegistrados: 0,
    viajesAsignados: 0,
    viajesCancelados: 0,
    viajesCanceladosPorCliente: 0,
    viajesCanceladosPorConductor: 0,
    viajesSinUnidad: 0
  });

  // Nuevo estado para el resumen de viajes de la operadora
  const [resumenViajesOperadora, setResumenViajesOperadora] = useState({
    viajesAsignados: 0,
    canceladosPorCliente: 0,
    canceladosPorConductor: 0,
    total: 0
  });

  // Disparador para abrir el modal de autenticación en TaxiForm
  const [authTrigger, setAuthTrigger] = useState(0);
  const solicitarAutenticacionOperadora = () => setAuthTrigger((v) => v + 1);
  const [mostrarChat, setMostrarChat] = useState(false);

  // --- ALERTAS (Firestore: "alertas") ---
  const seenAlertIdsRef = useRef(new Set());
  const alertTimersRef = useRef([]);

  const clearAlertTimers = useCallback(() => {
    try {
      (alertTimersRef.current || []).forEach((t) => clearTimeout(t));
    } catch (_) { }
    alertTimersRef.current = [];
  }, []);

  const playBeep = useCallback(() => {
    try {
      const Ctx = window.AudioContext || window.webkitAudioContext;
      if (!Ctx) return;
      const ctx = new Ctx();
      const osc = ctx.createOscillator();
      const gain = ctx.createGain();
      osc.connect(gain);
      gain.connect(ctx.destination);
      osc.frequency.value = 880;
      osc.type = 'sine';
      gain.gain.setValueAtTime(0.3, ctx.currentTime);
      gain.gain.exponentialRampToValueAtTime(0.01, ctx.currentTime + 0.35);
      osc.start(ctx.currentTime);
      osc.stop(ctx.currentTime + 0.35);
      setTimeout(() => ctx.close?.(), 600);
    } catch (e) {
      // Ignorar
    }
  }, []);

  const alertSound = useMemo(() => {
    try {
      return new Audio(process.env.PUBLIC_URL + '/assets/alert.mp3');
    } catch (_) {
      return null;
    }
  }, []);

  const playAlertSound = useCallback(() => {
    if (!alertSound) {
      playBeep();
      return;
    }
    alertSound.currentTime = 0;
    alertSound.play().catch(() => playBeep());
  }, [alertSound, playBeep]);

  useEffect(() => {
    // Listener en tiempo real (sin orderBy)
    const qAlertas = query(collection(db, 'alertas'), limit(100));
    const unsubscribe = onSnapshot(
      qAlertas,
      (snapshot) => {
        snapshot.docChanges().forEach((change) => {
          if (change.type !== 'added') return;
          const id = change.doc.id;
          if (seenAlertIdsRef.current.has(id)) return;
          seenAlertIdsRef.current.add(id);

          const data = change.doc.data() || {};
          const alertObj = { id, ...data };

          setActiveAlert(alertObj);
          playAlertSound();

          // Auto-cerrar UI luego de 10s
          alertTimersRef.current.push(setTimeout(() => setActiveAlert(null), 10000));

          // Auto-eliminar el doc luego de 5s
          alertTimersRef.current.push(setTimeout(async () => {
            try {
              await deleteDoc(change.doc.ref);
            } catch (e) {
              console.error('Error eliminando alerta:', e);
            }
          }, 5000));
        });
      },
      (error) => {
        console.error('ERROR EN FIREBASE (ALERTAS):', error);
      }
    );

    return () => {
      clearAlertTimers();
      unsubscribe();
    };
  }, [clearAlertTimers, playAlertSound]);

  // Restaurar operador autenticado desde localStorage al cargar
  useEffect(() => {
    try {
      const saved = localStorage.getItem('operadorAutenticado');
      if (saved) {
        const parsed = JSON.parse(saved);
        if (parsed && parsed.nombre) {
          setOperadorAutenticado(parsed);
        }
      }
    } catch (e) {
      console.error('Error leyendo operadorAutenticado desde localStorage:', e);
    }
  }, []);

  // Persistir cambios del operador en localStorage
  useEffect(() => {
    try {
      if (operadorAutenticado) {
        localStorage.setItem('operadorAutenticado', JSON.stringify(operadorAutenticado));
      } else {
        localStorage.removeItem('operadorAutenticado');
      }
    } catch (e) {
      console.error('Error guardando operadorAutenticado en localStorage:', e);
    }
  }, [operadorAutenticado]);

  // Función para cargar el resumen de viajes de la operadora (retorna la función de desuscripción)
  const cargarResumenViajesOperadora = (nombreOperadora) => {
    if (!nombreOperadora) return undefined;

    try {
      const qPedidosEnCurso = query(collection(db, 'pedidoEnCurso'));
      const unsubscribe = onSnapshot(qPedidosEnCurso, (querySnapshot) => {
        let asignados = 0;
        let canceladosPorCliente = 0;
        let canceladosPorConductor = 0;

        querySnapshot.forEach((docSnap) => {
          const pedido = docSnap.data();
          if (pedido && pedido.operadora === nombreOperadora) {
            const estado = pedido.pedido || pedido.estado || '';
            if (estado.includes('Aceptado') || estado.includes('En Curso') || estado.includes('Iniciado')) {
              asignados++;
            } else if (estado.includes('Cancelado por Cliente')) {
              canceladosPorCliente++;
            } else if (estado.includes('Cancelado por Unidad') || estado.includes('Cancelado por Conductor')) {
              canceladosPorConductor++;
            }
          }
        });

        setResumenViajesOperadora({
          viajesAsignados: asignados,
          canceladosPorCliente,
          canceladosPorConductor,
          total: asignados + canceladosPorCliente + canceladosPorConductor
        });
      });

      return unsubscribe;
    } catch (error) {
      console.error('❌ Error al cargar resumen de viajes de la operadora:', error);
      return undefined;
    }
  };

  // Efecto para cargar el resumen cuando cambie la operadora autenticada
  useEffect(() => {
    let unsubscribeRef;
    if (operadorAutenticado && operadorAutenticado.nombre) {
      unsubscribeRef = cargarResumenViajesOperadora(operadorAutenticado.nombre);
    } else {
      setResumenViajesOperadora({
        viajesAsignados: 0,
        canceladosPorCliente: 0,
        canceladosPorConductor: 0,
        total: 0
      });
    }

    return () => {
      if (typeof unsubscribeRef === 'function') {
        unsubscribeRef();
      }
    };
  }, [operadorAutenticado]);

  // Listener en tiempo real de reportesDiarios para el operador y la fecha actual
  useEffect(() => {
    let unsubscribeReporte;
    try {
      if (operadorAutenticado && operadorAutenticado.nombre) {
        const hoy = new Date();
        const fechaHoy = hoy
          .toLocaleDateString('es-EC', { day: '2-digit', month: '2-digit', year: 'numeric' })
          .replace(/\//g, '-');
        const reporteId = `${operadorAutenticado.nombre}_${fechaHoy}`;
        const reporteRef = doc(db, 'reportesDiarios', reporteId);
        unsubscribeReporte = onSnapshot(reporteRef, (snap) => {
          if (snap.exists()) {
            const data = snap.data();
            setReporteDiario({
              viajesRegistrados: data.viajesRegistrados || 0,
              viajesAsignados: data.viajesAsignados || 0,
              viajesCancelados: data.viajesCancelados || 0,
              viajesCanceladosPorCliente: data.viajesCanceladosPorCliente || 0,
              viajesCanceladosPorConductor: data.viajesCanceladosPorConductor || 0,
              viajesFinalizados: data.viajesFinalizados || 0,
              vouchersGenerados: data.vouchersGenerados || 0,
              viajesAutomaticos: data.viajesAutomaticos || 0,
            });
          } else {
            setReporteDiario({
              viajesRegistrados: 0,
              viajesAsignados: 0,
              viajesCancelados: 0,
              viajesCanceladosPorCliente: 0,
              viajesCanceladosPorConductor: 0,
              viajesFinalizados: 0,
              vouchersGenerados: 0,
              viajesAutomaticos: 0,
            });
          }
        });
      } else {
        setReporteDiario({
          viajesRegistrados: 0,
          viajesAsignados: 0,
          viajesCancelados: 0,
          viajesCanceladosPorCliente: 0,
          viajesCanceladosPorConductor: 0,
          viajesFinalizados: 0,
          vouchersGenerados: 0,
          viajesAutomaticos: 0,
        });
      }
    } catch (e) {
      console.error('Error escuchando reportesDiarios:', e);
    }

    return () => {
      if (typeof unsubscribeReporte === 'function') {
        unsubscribeReporte();
      }
    };
  }, [operadorAutenticado]);

  return (
    <div style={{
      display: 'flex',
      height: '100vh',
      fontFamily: 'Arial, sans-serif'
    }}>
      <AlertModal alert={activeAlert} onClose={() => { clearAlertTimers(); setActiveAlert(null); }} />
      <Sidebar
        activeSection={activeSection}
        setActiveSection={setActiveSection}
        isCollapsed={isCollapsed}
        setIsCollapsed={setIsCollapsed}
        cerrarSesionGeneral={() => {
          console.log('Cerrando sesión general del sistema');
        }}
      />
      <div
        onClick={() => {
          if (!isCollapsed) setIsCollapsed(true);
        }}
        style={{
          flex: 1,
          display: 'flex',
          flexDirection: 'column',
          overflow: 'hidden'
        }}>
        <Header
          activeSection={activeSection}
          isCollapsed={isCollapsed}
          setIsCollapsed={setIsCollapsed}
          operadorAutenticado={operadorAutenticado}
          reporteDiario={reporteDiario}
          resumenViajesOperadora={resumenViajesOperadora}
          onSolicitarAutenticacionOperadora={solicitarAutenticacionOperadora}
          cambiarUsuario={() => {
            setOperadorAutenticado(null);
            localStorage.removeItem('operadorAutenticado');
            setReporteDiario({
              viajesRegistrados: 0,
              viajesAsignados: 0,
              viajesCancelados: 0,
              viajesCanceladosPorCliente: 0,
              viajesCanceladosPorConductor: 0,
              viajesSinUnidad: 0
            });
            setResumenViajesOperadora({
              viajesAsignados: 0,
              canceladosPorCliente: 0,
              canceladosPorConductor: 0,
              total: 0
            });
          }}
        />
        <div style={{ display: 'flex', flex: 1, gap: mostrarChat ? 16 : 0, padding: 12, overflow: 'hidden' }}>
          {/* Panel de Chat a la izquierda */}
          {mostrarChat && (
            <div style={{ flexBasis: '30%', maxWidth: '30%', background: colors.surface, borderRight: `1px solid ${colors.border}`, padding: 12, overflowY: 'auto' }}>
              <div style={{ height: '100%', display: 'flex', flexDirection: 'column' }}>
                <ChatPanel chatId="central" />
              </div>
            </div>
          )}

          {/* Contenido Principal (Mapa) a la derecha */}
          <div style={{ flexBasis: mostrarChat ? '70%' : '100%', flexGrow: 1, maxWidth: mostrarChat ? '70%' : '100%', overflow: 'hidden', display: 'flex', flexDirection: 'column' }}>
            <MainContent
              activeSection={activeSection}
              alertaActiva={activeAlert}
              operadorAutenticado={operadorAutenticado}
              setOperadorAutenticado={setOperadorAutenticado}
              reporteDiario={reporteDiario}
              setReporteDiario={setReporteDiario}
              authTrigger={authTrigger}
              setAuthTrigger={setAuthTrigger}
              setIsCollapsed={setIsCollapsed}
            />
          </div>
        </div>

        {/* Botón flotante opcional para mostrar chat (puedes quitarlo si prefieres ocultación total) */}
        {!mostrarChat && (
          <button
            onClick={() => setMostrarChat(true)}
            style={{
              position: 'fixed',
              bottom: 30,
              left: isCollapsed ? 100 : 280,
              zIndex: 1001,
              background: '#3b82f6',
              color: 'white',
              border: 'none',
              borderRadius: '50%',
              width: '50px',
              height: '50px',
              cursor: 'pointer',
              boxShadow: '0 4px 12px rgba(0,0,0,0.2)',
              display: 'flex',
              alignItems: 'center',
              justifyContent: 'center',
              fontSize: '20px',
              transition: 'left 0.3s ease'
            }}
            title="Mostrar Chat"
          >
            💬
          </button>
        )}
        {mostrarChat && (
          <button
            onClick={() => setMostrarChat(false)}
            style={{
              position: 'fixed',
              bottom: 30,
              left: isCollapsed ? 100 : 280,
              zIndex: 1001,
              background: '#64748b',
              color: 'white',
              border: 'none',
              borderRadius: '50%',
              width: '40px',
              height: '40px',
              cursor: 'pointer',
              boxShadow: '0 4px 12px rgba(0,0,0,0.2)',
              display: 'flex',
              alignItems: 'center',
              justifyContent: 'center',
              fontSize: '16px',
              transition: 'left 0.3s ease'
            }}
            title="Ocultar Chat"
          >
            ✕
          </button>
        )}
      </div>
    </div>
  );
}

export default Dashboard; 